import { useNavigate } from "react-router-dom";
import { LogOut, Moon, Shield, Sun, User } from "lucide-react";
import { useLogout, useWhoami } from "@/api/auth";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { usePermissions } from "@/hooks/usePermissions";
import { useTheme } from "@/hooks/useTheme";
import { cn } from "@/lib/utils";
import { AdminUsersPanel } from "./AdminUsersPanel";

export function SettingsPage() {
  const navigate = useNavigate();
  const whoami = useWhoami();
  const logout = useLogout();
  const permissions = usePermissions();
  const { theme, setTheme } = useTheme();

  async function onSignOut() {
    await logout.mutateAsync();
    navigate("/login", { replace: true });
  }

  const user = whoami.data?.user ?? null;
  const role = whoami.data?.role ?? null;
  const authRequired = !!whoami.data?.auth_required;
  const authed = !!whoami.data?.authenticated;

  return (
    <div className="p-6 space-y-5 max-w-3xl">
      <header>
        <h2 className="text-2xl font-semibold text-foreground">Settings</h2>
        <p className="text-sm text-muted-foreground">
          Session and appearance preferences for this DemoSim runner.
        </p>
      </header>

      <Card className="p-5 space-y-4">
        <SectionHeader icon={<User size={14} />} title="Session" />
        <Field label="Signed in as" value={user ?? "(no auth required)"} mono />
        <Field label="Role" value={role ?? "(none)"} />
        <Field
          label="Auth"
          value={authRequired ? (authed ? "Authenticated" : "Required, not signed in") : "Disabled"}
        />
        <div className="pt-1">
          <Button
            onClick={onSignOut}
            variant="outline"
            disabled={logout.isPending || !authed}
          >
            <LogOut size={14} />
            {logout.isPending ? "Signing out…" : "Sign out"}
          </Button>
        </div>
      </Card>

      <Card className="p-5 space-y-4">
        <SectionHeader icon={<Sun size={14} />} title="Appearance" />
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-medium text-foreground">Theme</div>
            <p className="text-xs text-muted-foreground">
              Stored per-browser. Defaults to your OS preference on first visit.
            </p>
          </div>
          <div className="inline-flex rounded-md border border-border p-0.5">
            <ThemeOption
              active={theme === "light"}
              onClick={() => setTheme("light")}
              icon={<Sun size={14} />}
              label="Light"
            />
            <ThemeOption
              active={theme === "dark"}
              onClick={() => setTheme("dark")}
              icon={<Moon size={14} />}
              label="Dark"
            />
          </div>
        </div>
      </Card>

      <Card className="p-5 space-y-3">
        <SectionHeader icon={<Shield size={14} />} title="About" />
        <p className="text-sm text-muted-foreground">
          DemoSim runner — pushes synthetic observability data to real backends so the
          NeuBird agent can investigate scripted incidents.
        </p>
      </Card>

      <Card className="p-5 space-y-4">
        <SectionHeader icon={<Shield size={14} />} title="User Management" />
        {permissions.canManageUsers ? (
          <AdminUsersPanel />
        ) : (
          <p className="text-sm text-muted-foreground">
            {permissions.usersReason ?? "Only admins can manage users."}
          </p>
        )}
      </Card>
    </div>
  );
}

function SectionHeader({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {icon}
      {title}
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="grid grid-cols-[10rem_1fr] items-baseline gap-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={cn("text-sm text-foreground", mono && "font-mono")}>{value}</div>
    </div>
  );
}

function ThemeOption({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 px-3 h-7 rounded-sm text-xs font-medium transition-colors",
        active ? "bg-foreground text-background" : "text-foreground hover:bg-muted",
      )}
      aria-pressed={active}
    >
      {icon}
      {label}
    </button>
  );
}
